import { useEffect, useRef, useState } from "react";
import { BuzerIcon } from "../../BuzerIcons";
import { Icon } from "../../icons";
import { DateField, Glyph, MoodFace } from "../../components/ui";
import { ActionMenu, Button, Caption, EmptyState, IconButton, Input, Pill, Select, Sheet, Tag, Textarea } from "../../components/ui/ds";
import { api } from "../../lib/api";
import { JOURNAL_BACKFILL_DAYS, JOURNAL_TAG, JOURNAL_TAGS, MOODS, moodIconName } from "../../lib/constants";
import { fmtShortDate, todayKey, ymd } from "../../lib/format";
import { SK_MONTHS_NOM } from "../../lib/date-sk";
import { iconChipStyle } from "../../lib/icons-resolve";
import { st } from "../../styles.js";
import "./journal.css";

// DENNÍK DŇA (feature-spec-dennik.md §5–§7)
//
// Nálada a zápisy sú dve NEZÁVISLÉ veci (princíp 1): MoodPicker mení len náladu, editor len
// zápisy, a zrušenie nálady zápisy nikdy nezmaže. Text zápisu je T0 údaj — sem prichádza už
// dešifrovaný z API a nikam inam sa z klienta neposiela.
//
//   MoodPicker     — päť nálad (bez poľa „prečo?" — to je odteraz zápis)
//   JournalEditor  — jeden riadok + chipy „K návyku" a štítky (rozbalia sa až po fokuse)
//   DayJournal     — nálada + zápisy dňa (max 3 viditeľné) + editor + „Na tento deň"
//   EntryEditSheet — úprava / zmazanie jedného zápisu
//   JournalSheet   — Celý denník: hľadanie, filtre, karty dní, načítať staršie, export, súhlas
//
// Za zápis nie sú body (princíp 2) — body idú len cez návyk „splní sa zápismi" (`entry_goal`),
// a o tom rozhoduje server (`habit_done` v odpovedi POST /api/journal).

const DRAFT_KEY = (day) => "journal-draft:" + day;

function loadDraft(day) { try { return sessionStorage.getItem(DRAFT_KEY(day)) || ""; } catch { return ""; } }
function saveDraft(day, text) { try { if (text) sessionStorage.setItem(DRAFT_KEY(day), text); else sessionStorage.removeItem(DRAFT_KEY(day)); } catch { /* */ } }

export function canBackfill(day) {
  const d = new Date(day + "T00:00:00"), t = new Date(todayKey() + "T00:00:00");
  const diff = Math.round((t - d) / 86400000);
  return diff >= 0 && diff <= JOURNAL_BACKFILL_DAYS;
}

// najstarší deň, ku ktorému sa dá ešte dopísať (zrkadlí `canBackfill` aj server §4.2)
export function backfillMin() {
  const d = new Date(todayKey() + "T00:00:00");
  d.setDate(d.getDate() - JOURNAL_BACKFILL_DAYS);
  return ymd(d.getFullYear(), d.getMonth(), d.getDate());
}

const entryTime = (e) => (e.created_at ? e.created_at.slice(11, 16) : "");
// 'YYYY-MM' → prvý deň nasledujúceho mesiaca (parameter `before` je exkluzívny)
const nextMonthStart = (ym) => { const [y, m] = ym.split("-").map(Number); return m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`; };
const monthLabel = (ym) => { const [y, m] = ym.split("-").map(Number); return `${SK_MONTHS_NOM[m - 1]} ${y}`; };

// ── nálada ────────────────────────────────────────────────────────────────────────────────
// D2 — jeden zdieľaný mood-picker (denný aj spätný). Referenčný v33h dizajn: okrúhle 52px
// ikony, farba témy pri výbere. Pole „prečo?" odišlo do denníka (zápis dňa).
export function MoodPicker({ selected, onPick }) {
  return (
    <div style={{ ...st.moodRow, marginTop: 0 }}>
      {MOODS.map((m) => {
        const on = selected === m.id;
        return (
          <button key={m.id} type="button" onClick={() => onPick(m.id)} className="mood"
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 7, background: "none", border: "none", cursor: "pointer", padding: "4px 2px" }} title={m.label}>
            <span style={{ width: 52, height: 52, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: on ? "var(--brand-primary)" : "var(--neutral-surface-3)",
              border: on ? "1px solid var(--brand-primary)" : "1px solid var(--neutral-border)",
              transition: "background .18s, transform .18s", transform: on ? "translateY(-2px)" : "none" }}>
              <MoodFace k={m.icon} size={26} color={on ? "var(--brand-on-primary)" : "var(--neutral-text-main)"} />
            </span>
            <span style={{ fontSize: 10.5, fontWeight: on ? 700 : 600, color: on ? "var(--brand-primary)" : "var(--neutral-text-muted)", fontFamily: "var(--ff-ui)", textAlign: "center", lineHeight: 1.2 }}>{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Rozbaľovací výber návyku (natívny <select> cez primitív Select): jeden riadok, otvorí sa hneď.
function HabitSelect({ habits, onPick, placeholder = "— vyber návyk —" }) {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (el) setTimeout(() => { el.focus(); try { el.showPicker && el.showPicker(); } catch { /* */ } }, 30); }, []);
  return (
    <Select ref={ref} aria-label="Vyber návyk" value="" onChange={(e) => onPick(e.target.value ? Number(e.target.value) : null)}>
      <option value="">{placeholder}</option>
      {(habits || []).map((h) => <option key={h.id} value={h.id}>{h.label}</option>)}
    </Select>
  );
}

// ── chipy editora ─────────────────────────────────────────────────────────────────────────
// `K návyku` → po ťuku sa ukáže ROZBAĽOVACÍ VÝBER návykov (nesplnené prvé), nie zoznam chipov
// (rozhodnutie majiteľky 2026-09-07 pri náhľade); vybraný nahradí chip ikonou + názvom s „×".
// Štítky sú pevná šestica (§2.2). Návyk a štítok sa nevylučujú.
function ChipRow({ habits, taskId, onTask, tag, onTag }) {
  const [pick, setPick] = useState(false);
  const chosen = taskId ? (habits || []).find((h) => h.id === taskId) : null;
  return (
    <>
      <div className="bz-journal__chips">
        {chosen ? (
          <Pill on onClick={() => onTask(null)} title="Odobrať návyk">
            <span style={{ display: "inline-flex", width: 16, justifyContent: "center" }}><Glyph icon={chosen.icon} size={14} /></span>
            {chosen.label}
            <span className="bz-journal__chip-x"><BuzerIcon name="close" size={12} color="currentColor" /></span>
          </Pill>
        ) : (
          (habits || []).length > 0 && (
            <Pill on={pick} onClick={() => setPick((v) => !v)} aria-expanded={pick ? "true" : "false"}>
              <BuzerIcon name="add" size={13} color="currentColor" /> K návyku
            </Pill>
          )
        )}
        {JOURNAL_TAGS.map((t) => (
          <Pill key={t.key} on={tag === t.key} onClick={() => onTag(tag === t.key ? null : t.key)} title={t.label}>
            <BuzerIcon name={t.icon} size={13} color="currentColor" /> {t.label}
          </Pill>
        ))}
      </div>
      {pick && !chosen && (
        <HabitSelect habits={habits} onPick={(id) => { if (id) onTask(id); setPick(false); }} />
      )}
    </>
  );
}

// ── editor ────────────────────────────────────────────────────────────────────────────────
export function JournalEditor({ day, habits, question, onAdded, autoFocus = false, presetTaskId = null, compact = false, onCancel = null }) {
  const [text, setText] = useState(() => loadDraft(day));
  const [taskId, setTaskId] = useState(presetTaskId);
  const [tag, setTag] = useState(null);
  const [focus, setFocus] = useState(!!autoFocus || !!presetTaskId);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ref = useRef(null);
  useEffect(() => { setText(loadDraft(day)); }, [day]);
  useEffect(() => { if (autoFocus && ref.current) { const el = ref.current; setTimeout(() => el.focus(), 60); } }, [autoFocus]);
  const onChange = (v) => { setText(v); saveDraft(day, v); };
  async function submit() {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true); setErr("");
    try {
      const r = await api("/api/journal", { method: "POST", body: { day, text: t, task_id: taskId, tag } });
      saveDraft(day, "");
      setText(""); setTaskId(presetTaskId); setTag(null);   // druhý zápis nededí návyk prvého
      onAdded && onAdded(r);
      if (ref.current) ref.current.focus();
    } catch (e) { setErr((e && e.message) || "Nepodarilo sa uložiť."); }
    finally { setBusy(false); }
  }
  const showChips = focus || !!text;
  return (
    <div className="bz-journal__editor">
      <div className="bz-journal__line">
        <Textarea ref={ref} rows={1} value={text} maxLength={500}
          placeholder={question || "Napíš pár slov o dnešku…"} aria-label="Nový zápis"
          onFocus={() => setFocus(true)}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }} />
        {text.trim()
          ? <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "…" : "Pridať"}</Button>
          : onCancel && (
            <IconButton variant="ghost" aria-label="Zavrieť" title="Zavrieť" onClick={onCancel}>
              <BuzerIcon name="close" size={15} color="currentColor" />
            </IconButton>
          )}
      </div>
      {showChips && <ChipRow habits={habits} taskId={taskId} onTask={setTaskId} tag={tag} onTag={setTag} />}
      {err && <Caption variant="label" style={{ color: "var(--danger)" }}>{err}</Caption>}
      {!compact && showChips && !text && <Caption>Stačí jedna veta. Enter = Pridať.</Caption>}
    </div>
  );
}

// ── „+ Pridať zápis" ──────────────────────────────────────────────────────────────────────
// Zbalený editor (rozhodnutie majiteľky 2026-09-08). PRVÝ zápis dňa má pole rovno — vtedy je
// písanie tá jediná vec, ktorú od panelu chceš. Keď deň už zápis MÁ, prázdne pole pod ním len
// tlačí zoznam nadol, takže druhý a ďalší zápis začína tlačidlom. Rozbalený editor sa po
// pridaní NEZATVÁRA (fokus ostáva v poli — „tri vety o dnešku" sa píšu za sebou), zavrie ho
// × alebo zatvorenie panelu.
export function JournalAdd({ day, habits, question, onAdded, presetTaskId = null, compact = false,
                            open: openInit = false, label = "Pridať zápis" }) {
  const [open, setOpen] = useState(openInit);
  useEffect(() => { if (openInit) setOpen(true); }, [openInit]);
  if (open) {
    return (
      <JournalEditor day={day} habits={habits} question={question} onAdded={onAdded}
        presetTaskId={presetTaskId} compact={compact} autoFocus onCancel={() => setOpen(false)} />
    );
  }
  return (
    <Button variant="ghost" size="sm" className="bz-journal__add" onClick={() => setOpen(true)}>
      <BuzerIcon name="add" size={15} color="currentColor" /> {label}
    </Button>
  );
}

// ── riadok zápisu ─────────────────────────────────────────────────────────────────────────
export function EntryRow({ e, onClick, showDay = false }) {
  const tagMeta = e.tag ? JOURNAL_TAG[e.tag] : null;
  const lead = e.task
    ? <span className="bz-journal__lead" style={iconChipStyle(e.task.icon_color)} title={e.task.label}><Glyph icon={e.task.icon} size={13} /></span>
    : tagMeta
      ? <span className="bz-journal__lead bz-journal__lead--tag" title={tagMeta.label}><BuzerIcon name={tagMeta.icon} size={13} color="currentColor" /></span>
      : <span className="bz-journal__lead bz-journal__lead--dot" aria-hidden="true" />;
  return (
    <button type="button" className="bz-journal__row" onClick={onClick} aria-label="Upraviť zápis">
      {lead}
      <span className={"bz-journal__text" + (e.text == null ? " bz-journal__text--unreadable" : "")}>
        {e.text == null ? "Zápis sa nedá zobraziť" : e.text}
      </span>
      <span className="bz-journal__time">{showDay ? fmtShortDate(e.day) : entryTime(e)}</span>
    </button>
  );
}

// ── denník jedného dňa (dnešný modál nálady aj spätný deň) ────────────────────────────────
export function DayJournal({ day, mood, onPickMood, entries, question, throwback, habits, onChanged, onEdit, onOpenFull, onOpenDay, autoFocus = false, presetTaskId = null }) {
  const list = entries || [];
  const shown = list.slice(0, 3);
  const rest = list.length - shown.length;
  const editable = canBackfill(day);
  return (
    <div className="bz-journal">
      <MoodPicker selected={mood} onPick={onPickMood} />
      {list.length > 0 && (
        <div className="bz-journal__list">
          {shown.map((e) => <EntryRow key={e.id} e={e} onClick={() => onEdit && onEdit(e)} />)}
          {(rest > 0 || onOpenFull) && (
            <Button variant="ghost" size="sm" className="bz-journal__more" onClick={onOpenFull}>
              {rest > 0 ? `+ ${rest} ${rest === 1 ? "ďalší" : rest <= 4 ? "ďalšie" : "ďalších"} · ` : ""}Celý denník <Icon name="chevron" size={14} color="currentColor" />
            </Button>
          )}
        </div>
      )}
      {editable ? (
        <JournalAdd day={day} habits={habits} question={question} onAdded={onChanged}
          presetTaskId={presetTaskId} open={list.length === 0 || autoFocus || !!presetTaskId} />
      ) : (
        <Caption icon="calendar">Staršie dni sa už nedajú dopísať, existujúce zápisy sa dajú upraviť.</Caption>
      )}
      {list.length === 0 && onOpenFull && (
        <Button variant="ghost" size="sm" className="bz-journal__more" onClick={onOpenFull}>Celý denník <Icon name="chevron" size={14} color="currentColor" /></Button>
      )}
      {throwback && (
        <button type="button" className="bz-journal__throwback" onClick={() => onOpenDay && onOpenDay(throwback.day)} title={fmtShortDate(throwback.day)}>
          <BuzerIcon name="clock" size={14} color="currentColor" />
          <b>{throwback.ago === "year" ? "Pred rokom" : throwback.ago === "month" ? "Pred mesiacom" : "Pred týždňom"}:</b>
          <q>{throwback.text}</q>
        </button>
      )}
    </div>
  );
}

// ── úprava zápisu ─────────────────────────────────────────────────────────────────────────
// `deleteWarn` = text potvrdenia, keď by zmazanie zhodilo splnenie návyku „splní sa zápismi"
// (rozhoduje rodič — má počty dňa aj cieľ); bez neho sa maže rovno.
export function EntryEditSheet({ entry, habits, onClose, onSaved, onDeleted, deleteWarn }) {
  const [text, setText] = useState(entry.text || "");
  const [taskId, setTaskId] = useState(entry.task_id || null);
  const [tag, setTag] = useState(entry.tag || null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState("");
  const dirty = text.trim() !== (entry.text || "") || taskId !== (entry.task_id || null) || tag !== (entry.tag || null);
  async function save() {
    if (!text.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      const r = await api("/api/journal/" + entry.id, { method: "PATCH", body: { text: text.trim(), task_id: taskId, tag } });
      onSaved && onSaved(r.entry); onClose();
    } catch (e) { setErr((e && e.message) || "Nepodarilo sa uložiť."); }
    finally { setBusy(false); }
  }
  async function del() {
    if (busy) return;
    if (deleteWarn && !confirm) { setConfirm(true); return; }
    setBusy(true);
    try { await api("/api/journal/" + entry.id, { method: "DELETE" }); onDeleted && onDeleted(entry); onClose(); }
    catch (e) { setErr((e && e.message) || "Nepodarilo sa zmazať."); setBusy(false); }
  }
  // archivovaný návyk, ktorý už v ponuke nie je, musí ostať vybrateľný (zápis o ňom ostáva)
  const options = entry.task && !(habits || []).some((h) => h.id === entry.task_id)
    ? [...(habits || []), { id: entry.task_id, icon: entry.task.icon, label: entry.task.label }] : habits;
  return (
    <Sheet center title="Zápis" subtitle={fmtShortDate(entry.day) + (entryTime(entry) ? " · " + entryTime(entry) : "")} icon="feather" onClose={onClose}
      footer={
        <div style={{ display: "flex", flexDirection: "column", gap: 9, width: "100%" }}>
          {confirm ? (
            <>
              <Caption variant="label" style={{ color: "var(--danger)" }}>{deleteWarn}</Caption>
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="ghost" block onClick={() => setConfirm(false)}>Nechať</Button>
                <Button variant="destructive" block onClick={del} disabled={busy}>Zmazať aj tak</Button>
              </div>
            </>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="primary" block onClick={save} disabled={busy || !dirty || !text.trim()}>Uložiť</Button>
              <IconButton variant="ghost" aria-label="Zmazať zápis" title="Zmazať zápis" onClick={del} style={{ color: "var(--danger)" }}><Icon name="delete" size={16} color="currentColor" /></IconButton>
            </div>
          )}
        </div>
      }>
      <div className="bz-journal__editor">
        <Textarea rows={4} value={text} maxLength={500} onChange={(e) => setText(e.target.value)} aria-label="Text zápisu" />
        <ChipRow habits={options} taskId={taskId} onTask={setTaskId} tag={tag} onTag={setTag} />
        {err && <Caption variant="label" style={{ color: "var(--danger)" }}>{err}</Caption>}
      </div>
    </Sheet>
  );
}

// ── zápis k vybranému dňu ─────────────────────────────────────────────────────────────────
// Karty v Celom denníku sú len dni, ktoré UŽ zápis majú, takže prázdny včerajšok sa inak nedá
// chytiť. Deň sa vyberá v popupe (rozhodnutie majiteľky 2026-09-08) — kalendár aj pole tak
// nezaberajú miesto v zozname a je jasné, že ide o jednu uzavretú akciu.
function AddDaySheet({ habits, onClose, onAdded }) {
  const [day, setDay] = useState(todayKey());
  return (
    <Sheet center title="Nový zápis" icon="feather" onClose={onClose}>
      <div className="bz-journal-addday__body">
        <div className="bz-journal-addday__pick">
          <BuzerIcon name="calendar" size={15} color="var(--neutral-text-muted)" />
          <DateField value={day} onChange={(v) => setDay(v || todayKey())}
            min={backfillMin()} max={todayKey()} label="Deň zápisu" />
        </div>
        {/* `key` = deň: pri prepnutí dňa sa editor založí odznova (rozpísaný text patrí dňu) */}
        <JournalEditor key={day} day={day} habits={habits} autoFocus
          question={day === todayKey() ? undefined : "Dopísať k tomuto dňu…"}
          onAdded={() => { onAdded(day); onClose(); }} />
      </div>
    </Sheet>
  );
}

// ── Celý denník ───────────────────────────────────────────────────────────────────────────
export function JournalSheet({ onClose, habits, initialTaskId = null, initialDay = null, journalAi = false, premium = false, onToggleAi, onEdit, onChanged, refreshKey = 0, onWriteToday, aiName = "Kouč" }) {
  const [q, setQ] = useState("");
  const [taskId, setTaskId] = useState(initialTaskId);
  const [tag, setTag] = useState(null);
  const [days, setDays] = useState(null);
  const [next, setNext] = useState(null);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [pickHabit, setPickHabit] = useState(false);
  // rok zápisov: skok na mesiac (select mesiacov so zápismi) namiesto dvanástich „Načítať staršie"
  const [monthsList, setMonthsList] = useState([]);
  const [jump, setJump] = useState("");   // 'YYYY-MM' | ''
  // zápis k dňu, ktorý v zozname ešte nie je (karty sú len dni SO zápismi) — vlastný výber dňa
  const [addDay, setAddDay] = useState(false);
  const [menu, setMenu] = useState(false);   // ⋮ — export a súhlas AI (v hlavičke, nie v tele)
  const [bump, setBump] = useState(0);    // po zápise k novému dňu sa zoznam natiahne odznova
  const dayRefs = useRef({});
  useEffect(() => { api("/api/journal/months").then((r) => setMonthsList(r.months || [])).catch(() => {}); }, [refreshKey]);
  const jumpBefore = jump ? nextMonthStart(jump) : null;
  const params = (before) => {
    const p = new URLSearchParams();
    if (q.trim().length >= 2) p.set("q", q.trim());
    if (taskId) p.set("task_id", String(taskId));
    if (tag) p.set("tag", tag);
    if (before || jumpBefore) p.set("before", before || jumpBefore);
    const s = p.toString();
    return s ? "?" + s : "";
  };
  useEffect(() => {
    let live = true;
    const h = setTimeout(() => {
      setBusy(true);
      api("/api/journal" + params()).then((r) => { if (!live) return; setDays(r.days); setNext(r.next_before); setTotal(r.total_entries); })
        .catch(() => { if (live) setDays([]); }).finally(() => { if (live) setBusy(false); });
    }, q ? 300 : 0);
    return () => { live = false; clearTimeout(h); };
  }, [q, taskId, tag, refreshKey, jump, bump]);
  // deep-link na deň (throwback): po načítaní doscrolluj na kartu toho dňa
  useEffect(() => {
    if (!initialDay || !days) return;
    const el = dayRefs.current[initialDay];
    if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [days, initialDay]);
  // po pridaní zápisu: prekresli kartu toho dňa (deň v zozname) alebo natiahni zoznam odznova
  // (deň, ktorý kartu ešte nemal), a nech si appka zvonku prepočíta body — zápis môže splniť
  // návyk „splní sa zápismi"
  function afterAdd(day) {
    onChanged && onChanged();
    if (!(days || []).some((d) => d.day === day)) { setBump((b) => b + 1); return; }
    api("/api/journal?day=" + day)
      .then((r) => setDays((ds) => (ds || []).map((x) => (x.day === day ? { ...x, entries: r.entries } : x))))
      .catch(() => {});
  }
  async function more() {
    if (!next || busy) return;
    setBusy(true);
    try { const r = await api("/api/journal" + params(next)); setDays((d) => [...(d || []), ...r.days]); setNext(r.next_before); }
    finally { setBusy(false); }
  }
  const chosen = taskId ? (habits || []).find((h) => h.id === taskId) : null;
  // návyky do filtra: aktívne + tie, ktoré má nejaký zápis v načítanom denníku (aj archivované)
  const filterHabits = (() => {
    const seen = new Map((habits || []).map((h) => [h.id, h]));
    (days || []).forEach((d) => d.entries.forEach((e) => { if (e.task && !seen.has(e.task_id)) seen.set(e.task_id, { id: e.task_id, icon: e.task.icon, label: e.task.label }); }));
    return [...seen.values()];
  })();
  const empty = days && days.length === 0 && !q && !taskId && !tag;
  return (
    <Sheet full title="Denník" icon="book" onClose={onClose}
      headerRight={<>
        {total > 0 && <Tag tone="neutral" size="xs">{total}</Tag>}
        <IconButton variant="ghost" size="sm" icon="dots" aria-label="Viac" title="Viac" onClick={() => setMenu(true)} />
      </>}>
      <div className="bz-journal-filters">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hľadať v zápisoch…" aria-label="Hľadať v zápisoch" />
        <div className="bz-journal__chips">
          {chosen || (taskId && !chosen) ? (
            <Pill on onClick={() => setTaskId(null)} title="Zrušiť filter návyku">
              {chosen && <span style={{ display: "inline-flex", width: 16, justifyContent: "center" }}><Glyph icon={chosen.icon} size={14} /></span>}
              {chosen ? chosen.label : "Návyk"}
              <span className="bz-journal__chip-x"><BuzerIcon name="close" size={12} color="currentColor" /></span>
            </Pill>
          ) : (
            filterHabits.length > 0 && <Pill on={pickHabit} onClick={() => setPickHabit((v) => !v)}>Návyk <Icon name="chevron" size={12} color="currentColor" /></Pill>
          )}
          {JOURNAL_TAGS.map((t) => (
            <Pill key={t.key} on={tag === t.key} onClick={() => setTag(tag === t.key ? null : t.key)} title={t.label}>
              <BuzerIcon name={t.icon} size={13} color="currentColor" />
            </Pill>
          ))}
        </div>
        {pickHabit && !taskId && (
          <HabitSelect habits={filterHabits} placeholder="— filtrovať podľa návyku —" onPick={(id) => { if (id) setTaskId(id); setPickHabit(false); }} />
        )}
        {monthsList.length > 1 && (
          <Select aria-label="Skočiť na mesiac" value={jump} onChange={(e) => setJump(e.target.value)}>
            <option value="">Od najnovšieho</option>
            {monthsList.map((m) => <option key={m.month} value={m.month}>{monthLabel(m.month)} · {m.count}</option>)}
          </Select>
        )}
        {/* zápis k ľubovoľnému dňu patrí NAD zoznam (rozhodnutie majiteľky 2026-09-08) —
            v pätičke pod rokom zápisov by ho nikto nenašiel */}
        <Button variant="ghost" size="sm" className="bz-journal__add" onClick={() => setAddDay(true)}>
          <BuzerIcon name="add" size={15} color="currentColor" /> Zápis k vybranému dňu
        </Button>
      </div>
      {days === null ? (
        <div style={{ ...st.taskDesc, padding: "14px 0" }}>Načítavam…</div>
      ) : empty ? (
        <EmptyState icon="feather" title="Prvý zápis sa objaví tu." desc="Pár slov o dnešku stačí — nálada a zápis sú dve nezávislé veci, píš aj bez nálady.">
          {onWriteToday && <Button variant="primary" onClick={() => { onClose(); onWriteToday(); }}>Napísať dnešný</Button>}
        </EmptyState>
      ) : days.length === 0 ? (
        <div style={{ ...st.taskDesc, padding: "14px 0" }}>Nič sa nenašlo.</div>
      ) : (
        days.map((d) => (
          <div key={d.day} className="bz-journal-day" ref={(el) => { dayRefs.current[d.day] = el; }}>
            <div className="bz-journal-day__head">
              {d.mood_id && <MoodFace k={moodIconName(d.mood_id)} size={16} />}
              <span className="bz-journal-day__date">{fmtShortDate(d.day)}</span>
              {d.mood_id && <span style={{ fontWeight: 500 }}>· {(MOODS.find((m) => m.id === d.mood_id) || {}).label}</span>}
            </div>
            <div className="bz-journal__list">
              {d.entries.map((e) => <EntryRow key={e.id} e={e} onClick={() => onEdit && onEdit(e)} />)}
            </div>
            {canBackfill(d.day) && (
              <JournalAdd day={d.day} habits={habits} compact onAdded={() => afterAdd(d.day)}
                question={d.day === todayKey() ? undefined : "Dopísať k tomuto dňu…"} />
            )}
          </div>
        ))
      )}
      {addDay && <AddDaySheet habits={habits} onClose={() => setAddDay(false)} onAdded={afterAdd} />}
      {next && (
        <div className="bz-journal-foot">
          <Button variant="ghost" block onClick={more} disabled={busy}>{busy ? "…" : "Načítať staršie"}</Button>
        </div>
      )}
      {/* ⋮ — export a súhlas AI. Obe sú „raz za čas" veci: v tele pod rokom zápisov len
          zaberali miesto (rozhodnutie majiteľky 2026-09-08). §11/3 — súhlas ostáva viditeľný
          aj mimo Premium (nech je jasné, čo sa odomkne), len sa nedá prepnúť. */}
      {menu && (
        <ActionMenu title="Denník" onClose={() => setMenu(false)} items={[
          { icon: "book", label: "Stiahnuť ako text", desc: "Všetky zápisy do jedného súboru",
            onClick: () => { window.location.href = "/api/journal/export"; } },
          { icon: "sparkles", label: `${aiName} smie čítať môj denník`,
            desc: premium
              ? "Použije ho len na Zrkadlo týždňa. Nikdy ho nikomu neposiela ďalej."
              : "Odomkne sa v Premium.",
            toggle: { on: !!journalAi, disabled: !premium },
            onClick: () => onToggleAi && onToggleAi(!journalAi) },
        ]} />
      )}
    </Sheet>
  );
}

// ── načítanie zápisov iného dňa (spätný modál nálady, heat mapa) ─────────────────────────
export function useDayEntries(day, refreshKey = 0) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!day) { setData(null); return undefined; }
    let live = true;
    api("/api/journal?day=" + day).then((r) => { if (live) setData(r); }).catch(() => { if (live) setData({ day, entries: [], mood_id: null }); });
    return () => { live = false; };
  }, [day, refreshKey]);
  return data;
}
